import {
  TabGroup as TabGroupPrimitive,
  Tab as TabLabelPrimitive,
} from '@headlessui/react';
import {
  ChevronDownIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@langchain/untitled-ui-icons';
import {
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
} from '@mui/joy';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { TableSkeleton } from '@/Pages/Home/ResourceTable/TableSkeleton';
import { DeleteModal } from '@/components/Delete';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/Dropdown';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { SecretInput } from '@/components/SecretInput';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/Table';
import { TabList } from '@/components/Tabs/Tabs';
import useToast from '@/components/Toast/useToast';
import { Tooltip } from '@/design-system';
import { useAuth } from '@/hooks/useAuth/useAuth';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { usePermissions } from '@/hooks/usePermissions';
import { useStateFromSearchParams } from '@/hooks/useStateFromSearchParams';
import {
  useCurrentOrganization,
  useOrganizationCreateMember,
  useOrganizationPatchMember,
  usePatchTenantMember,
  useRoles,
  useTenantList,
} from '@/hooks/useSwr';
import EditIcon from '@/icons/EditIcon.svg?react';
import TrashIcon from '@/icons/TrashIcon.svg?react';
import {
  IdentitySchema,
  OrganizationCreateMemberRequest,
  OrganizationRoleSchema,
  PendingIdentitySchema,
} from '@/types/schema';
import {
  apiOrganizationsPath,
  apiWorkspacesPath,
  mixedLoginMethods,
} from '@/utils/constants';
import { isSelfHostedSSO } from '@/utils/is-self-hosted';
import { cn } from '@/utils/tailwind';

import {
  MemberGroupType,
  MemberStatus,
} from '../types/organizationMembers.type';
import { ACTIVE_PAGE, PENDING_PAGE, useMembers } from '../utils/useMembers';
import { InviteOrgMemberModal } from './InviteOrgMemberModal';
import { WorkspaceMemberModal } from './InviteWorkspaceMemberModal';
import { MemberSettingsModal } from './MemberSettingsModal';
import { UpsellBadge } from './UpsellBadge';

const TenantMemberBasicAuthForm = (props: {
  identity?: IdentitySchema;
  onSuccess?: () => void;
}) => {
  'use no memo';
  const form = useForm<OrganizationCreateMemberRequest>({
    defaultValues: props.identity
      ? { email: props.identity.email ?? undefined }
      : undefined,
  });
  const create = useOrganizationCreateMember({
    onSuccess: props.onSuccess,
  });

  const update = useOrganizationPatchMember(props.identity?.id, {
    onSuccess: props.onSuccess,
  });

  const onSubmit = form.handleSubmit((json) => {
    if (props.identity) {
      update.trigger({ json });
    } else {
      create.trigger({ json });
    }
  });

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      {create.error && <ExpandableErrorAlert error={create.error} />}
      {update.error && <ExpandableErrorAlert error={update.error} />}
      <FormControl>
        <FormLabel>Email</FormLabel>
        <Input
          type="email"
          disabled={!!props.identity}
          {...form.register('email')}
        />
      </FormControl>

      <FormControl error={!!form.formState.errors.password?.message}>
        <FormLabel>Password</FormLabel>
        <SecretInput
          {...form.register('password', {
            minLength: {
              value: 12,
              message: 'Password must be at least 12 characters long',
            },
            validate: {
              lowercase: (value) =>
                /[a-z]/.test(value) ||
                'Password must have at least one lowercase letter',
              uppercase: (value) =>
                /[A-Z]/.test(value) ||
                'Password must have at least one uppercase letter',
              symbol: (value) =>
                `!#$%()+,-./:?@[\\]^_{~}`
                  .split('')
                  .some((symbol) => value.includes(symbol)) ||
                'Password must have at least one symbol',
            },
          })}
        />
        <FormHelperText>
          Password must be at least 12 characters long and have at least one
          lowercase, uppercase, and symbol.
        </FormHelperText>
      </FormControl>

      <Button type="submit" loading={create.isMutating || update.isMutating}>
        Save
      </Button>
    </form>
  );
};

function TenantDeleteMember(
  props: { groupType: 'organization' | 'workspace' } & (
    | { identity: IdentitySchema; type: 'member' }
    | { identity: PendingIdentitySchema; type: 'pending' }
  )
) {
  const [open, setOpen] = useState(false);
  const { createToast } = useToast();

  const prefix: string =
    props.groupType === 'organization'
      ? apiOrganizationsPath
      : apiWorkspacesPath;
  const suffix: string = props.type === 'member' ? '' : '/pending';

  const endpoint = `${prefix}/current/members/${props.identity.id}${suffix}`;

  const onError = (error: Error) => {
    console.error(`On delete member error: ${error.message}`);
    setOpen(false);
    createToast({ title: `${error.message}`, error: true });
  };

  return (
    <>
      <DeleteModal
        endpoint={endpoint}
        isOpen={open}
        doClose={() => setOpen(false)}
        onError={onError}
        invalidationPrefixes={[`${prefix}/current/members/active`]}
      />
      <button
        aria-label="delete"
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md p-1 transition-colors hover:bg-secondary-hover active:bg-secondary"
      >
        <TrashIcon className="h-5 w-5 text-tertiary" />
      </button>
    </>
  );
}

function TenantUpdateMember(props: { identity: IdentitySchema }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md p-0.5 transition-colors hover:bg-secondary-hover active:bg-secondary"
      >
        <EditIcon className="h-6 w-6 text-tertiary" />
      </button>

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog variant="outlined" sx={{ maxWidth: 480 }}>
          <ModalClose size="lg" />

          <div className="flex w-full flex-col px-4">
            <h2 className="mb-4 text-lg font-semibold">Update User</h2>

            <TenantMemberBasicAuthForm
              identity={props.identity}
              onSuccess={() => setOpen(false)}
            />
          </div>
        </ModalDialog>
      </Modal>
    </>
  );
}

function MemberRoleSwitchItem(props: {
  identity: IdentitySchema | PendingIdentitySchema;
  role: OrganizationRoleSchema;
  onSuccess?: () => void;
  groupType: 'organization' | 'workspace';
}) {
  const patchTenantMember = usePatchTenantMember({
    onSuccess: props.onSuccess,
  });
  const patchOrgMember = useOrganizationPatchMember(props.identity.id, {
    onSuccess: props.onSuccess,
  });

  return (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault();
        if (props.groupType === 'organization') {
          patchOrgMember.trigger({
            json: {
              identity_id: props.identity.id,
              role_id: props.role.id,
            },
          });
        } else {
          patchTenantMember.trigger({
            identity_id: props.identity.id,
            role_id: props.role.id,
          });
        }
      }}
      className="flex justify-between gap-2"
    >
      <span>{props.role.display_name}</span>

      <span
        className={cn(
          'flex opacity-0',
          (patchOrgMember.isMutating || patchTenantMember.isMutating) &&
            'opacity-100'
        )}
      >
        <CircularProgress size="sm" />
      </span>
    </DropdownMenuItem>
  );
}

function MemberRoleSwitch(props: {
  identity: IdentitySchema | PendingIdentitySchema;
  disabled?: boolean;
  groupType: 'organization' | 'workspace';
}) {
  const [open, setOpen] = useState(false);
  const rolesQuery = useRoles(
    props.groupType === 'organization' ? 'organization' : 'workspace'
  );
  const toast = useToast();
  const roles = rolesQuery.data;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        disabled={props.disabled}
        className="group flex items-center gap-1.5 outline-none"
      >
        <span className="whitespace-nowrap">{props.identity.role_name}</span>
        {!props.disabled && (
          <ChevronDownIcon className="h-5 w-5 transition-transform group-data-[state=open]:rotate-180" />
        )}
      </DropdownMenuTrigger>
      {!props.disabled && (
        <DropdownMenuContent align="start">
          {roles?.map((role) => {
            return (
              <MemberRoleSwitchItem
                key={role.id}
                role={role}
                identity={props.identity}
                onSuccess={() => {
                  toast.createToast(
                    "Updated user's role. Changes may take a couple minutes to propagate."
                  );
                  setOpen(false);
                }}
                groupType={props.groupType}
              />
            );
          })}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
}

type MemberRowProps = {
  canManage: boolean;
  allowRoleChange: boolean;
  groupType: 'organization' | 'workspace';
  currentUserId: string | undefined;
  showWorkspaces?: boolean;
} & (
  | { isPending: true; identity: PendingIdentitySchema }
  | { isPending: false; identity: IdentitySchema }
);

const MemberRow = ({
  identity,
  allowRoleChange,
  canManage,
  groupType,
  currentUserId,
  isPending,
  showWorkspaces = true,
}: MemberRowProps) => {
  const { data, isLoading: tenantsLoading } = useTenantList();
  const { data: orgRoles, isLoading: orgRolesLoading } =
    useRoles('organization');
  const workspaces =
    data?.filter((w) => identity.tenant_ids?.includes(w.id)) || [];

  const isOrgAdmin = identity.org_role_id
    ? Boolean(
        orgRoles?.find(
          (r) =>
            r.id === identity.org_role_id && r.name === 'ORGANIZATION_ADMIN'
        )
      )
    : false;

  if (tenantsLoading || orgRolesLoading) {
    return null;
  }

  let deleteMemberButton: JSX.Element | null = null;
  if (canManage) {
    if (isPending) {
      if (!isOrgAdmin) {
        deleteMemberButton = (
          <TenantDeleteMember
            type={'pending'}
            identity={identity}
            groupType={groupType}
          />
        );
      }
    } else if (identity.user_id !== currentUserId && !isOrgAdmin) {
      deleteMemberButton = (
        <TenantDeleteMember
          type={'member'}
          identity={identity}
          groupType={groupType}
        />
      );
    }
  }

  return (
    <TableRow key={identity.id}>
      <TableCell className="w-[50%] pl-5">
        <div className="flex items-center gap-2">
          <EmailSpan email={identity.email || ''} />
          {isPending ? (
            <span className="flex items-center gap-1.5 rounded-md border border-secondary px-2 py-0.5 text-sm">
              <span className="block h-1.5 w-1.5 rounded-full bg-ls-orange-500" />
              Invited
            </span>
          ) : (
            <span className="flex items-center gap-1.5 rounded-md border border-secondary px-2 py-0.5 text-sm">
              <span className="block h-1.5 w-1.5 rounded-full bg-success-strong" />
              Active
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="w-[20%] truncate">
        <div className="flex items-center gap-2">
          <MemberRoleSwitch
            disabled={
              isPending ||
              identity.user_id === currentUserId ||
              !canManage ||
              !allowRoleChange ||
              isOrgAdmin
            }
            identity={identity}
            groupType={groupType}
          />
          {isOrgAdmin && (
            <Tooltip
              title={
                <div className="w-[200px]">
                  The user is an org admin, change their role to user to edit
                  workspace roles.
                </div>
              }
            >
              <InformationCircleIcon height={20} className="cursor-pointer" />
            </Tooltip>
          )}
        </div>
      </TableCell>
      {groupType === MemberGroupType.ORGANIZATION && showWorkspaces && (
        <TableCell className="w-[20%]">
          {workspaces?.length > 1 ? (
            <Tooltip
              title={workspaces.map((w) => (
                <div>{w.display_name}</div>
              ))}
            >
              <span>{workspaces.length} workspaces</span>
            </Tooltip>
          ) : (
            workspaces[0]?.display_name || '0 workspaces'
          )}
        </TableCell>
      )}
      {canManage && (
        <TableCell className="w-[10%] pr-5">
          <div className="flex flex-shrink-0 items-center justify-center gap-2 whitespace-nowrap">
            {mixedLoginMethods.basic &&
              canManage &&
              groupType === MemberGroupType.ORGANIZATION &&
              !isPending && <TenantUpdateMember identity={identity} />}
            {deleteMemberButton}
            {isOrgAdmin && (
              <Tooltip
                title={
                  <div className="w-[200px]">
                    The user is an org admin, change their role to user to
                    remove them.
                  </div>
                }
              >
                <InformationCircleIcon height={20} className="cursor-pointer" />
              </Tooltip>
            )}
          </div>
        </TableCell>
      )}
    </TableRow>
  );
};
type Props = {
  canManage: boolean;
  isRbacEnabled: boolean;
  currentUserId?: string;
  members: IdentitySchema[];
  pendingMembers: PendingIdentitySchema[];
  groupType: 'organization' | 'workspace';
  showWorkspaces?: boolean;
};

const EmailSpan = ({ email }: { email: string }) => {
  if (email.length > 35) {
    return (
      <Tooltip title={email}>
        <span className="max-w-[270px] overflow-hidden text-ellipsis">
          {email}
        </span>
      </Tooltip>
    );
  }
  return (
    <span className="max-w-[270px] overflow-hidden text-ellipsis">{email}</span>
  );
};

const MembersTable = ({
  canManage,
  isRbacEnabled,
  currentUserId,
  members = [],
  pendingMembers = [],
  groupType,
  showWorkspaces = true,
}: Props) => {
  const showRoleChangeBadge =
    groupType === MemberGroupType.ORGANIZATION ? false : !isRbacEnabled;
  const allowRoleChange =
    groupType === MemberGroupType.ORGANIZATION
      ? canManage
      : isRbacEnabled && canManage;

  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[50%] pl-5">Email</TableHead>
          <TableHead className="w-[20%] align-middle">
            <div className="flex items-center">
              Role
              {showRoleChangeBadge ? (
                <UpsellBadge
                  tooltipMsg={
                    'Role based access control is only available for enterprise customers. Contact our sales team for more'
                  }
                  tier="ENTERPRISE"
                  linkProps={{
                    to: 'https://www.langchain.com/contact-sales',
                    target: '_blank',
                  }}
                  className="ml-2"
                />
              ) : (
                <Tooltip title="Changes to roles and permissions may take a couple minutes to propagate">
                  <div>
                    <InformationCircleIcon
                      height={20}
                      className="ml-2 cursor-pointer"
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          </TableHead>
          {groupType === MemberGroupType.ORGANIZATION && showWorkspaces && (
            <TableHead className="w-[20%] pr-5">Workspaces</TableHead>
          )}
          {canManage && (
            <TableHead className="w-[10%] pr-5 text-center">Actions</TableHead>
          )}
        </TableRow>
      </TableHeader>
      {members.length === 0 && pendingMembers.length === 0 && (
        <TableBody>
          <TableRow>
            <TableCell
              colSpan={canManage ? 4 : 3}
              className="h-32 text-center text-sm text-tertiary"
            >
              No members found
            </TableCell>
          </TableRow>
        </TableBody>
      )}
      <TableBody>
        {pendingMembers.map((identity) => (
          <MemberRow
            key={identity.id}
            isPending={true}
            identity={identity}
            canManage={canManage}
            allowRoleChange={allowRoleChange}
            groupType={groupType}
            currentUserId={currentUserId}
            showWorkspaces={showWorkspaces}
          />
        ))}
        {members.map((identity) => (
          <MemberRow
            key={identity.id}
            isPending={false}
            identity={identity}
            canManage={canManage}
            allowRoleChange={allowRoleChange}
            groupType={groupType}
            currentUserId={currentUserId}
            showWorkspaces={showWorkspaces}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export function MembersTableWithTabs(props: {
  showWorkspaces?: boolean;
  type: MemberGroupType;
}) {
  const [, setSearchParams] = useSearchParams();
  const [tab, setTab] = useStateFromSearchParams(
    'memberTab',
    MemberStatus.ACTIVE,
    setSearchParams
  );
  const {
    members: { data: team, isLoading },
    setPage,
    page,
    hasMore,
  } = useMembers(tab as MemberStatus, props.type, setSearchParams);

  const { showWorkspaces = true } = props;
  const { value: isRbacEnabled } = useOrgConfig(OrgConfigs.can_use_rbac);
  const { data: currentOrg } = useCurrentOrganization();

  const auth = useAuth();
  const { authorize } = usePermissions(
    props.type === MemberGroupType.ORGANIZATION ? 'organization' : 'workspace'
  );

  const canManage = authorize(
    `${
      props.type === MemberGroupType.ORGANIZATION
        ? 'organization'
        : 'workspaces'
    }:manage`
  );

  const onInviteSuccess = useCallback(() => {
    setTab('pending');
  }, [setTab]);

  const inviteModal = useMemo(() => {
    if (
      isSelfHostedSSO &&
      props.type === MemberGroupType.ORGANIZATION &&
      currentOrg?.default_sso_provision
    ) {
      return <MemberSettingsModal />;
    }
    return props.type === MemberGroupType.ORGANIZATION ? (
      <InviteOrgMemberModal onInviteSuccess={onInviteSuccess} />
    ) : (
      <WorkspaceMemberModal />
    );
  }, [props.type, currentOrg?.default_sso_provision, onInviteSuccess]);

  return (
    <div className="rounded-lg border border-secondary">
      <TabGroupPrimitive
        selectedIndex={tab === MemberStatus.ACTIVE ? ACTIVE_PAGE : PENDING_PAGE}
        onChange={(index) => {
          setTab(
            index === ACTIVE_PAGE ? MemberStatus.ACTIVE : MemberStatus.PENDING
          );
          setPage(0);
        }}
      >
        <TabList className="m-0 flex justify-between border-b border-secondary px-5 py-4">
          <div className="flex gap-4">
            <TabLabelPrimitive className="p-2 data-[selected]:border-b-2 data-[selected]:border-gray-800 data-[selected]:font-semibold dark:data-[selected]:border-gray-200">
              Active
            </TabLabelPrimitive>
            {!(isSelfHostedSSO && currentOrg?.default_sso_provision) && (
              <TabLabelPrimitive className="p-2 data-[selected]:border-b-2 data-[selected]:border-gray-800 data-[selected]:font-semibold dark:data-[selected]:border-gray-200">
                Pending
              </TabLabelPrimitive>
            )}
          </div>
          {canManage && inviteModal}
        </TabList>

        {isLoading ? (
          <div className="flex flex-grow items-center justify-center">
            <div className="w-full">
              <TableSkeleton rows={10} rowHeight={48} className="border-none" />
            </div>
          </div>
        ) : (
          <MembersTable
            canManage={canManage ?? false}
            isRbacEnabled={!!isRbacEnabled}
            currentUserId={auth.session?.user.id}
            members={
              tab === MemberStatus.ACTIVE ? (team as IdentitySchema[]) : []
            }
            pendingMembers={
              tab === MemberStatus.PENDING
                ? (team as PendingIdentitySchema[])
                : []
            }
            groupType={props.type}
            showWorkspaces={showWorkspaces}
          />
        )}
      </TabGroupPrimitive>
      {(hasMore || page > 0) && (
        <div className="flex items-center justify-end gap-2 border-t border-secondary px-5 py-4">
          <div>Page {page + 1}</div>
          <button
            type="button"
            data-testid="previous-page-button"
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-secondary hover:bg-secondary-hover active:bg-secondary disabled:bg-transparent disabled:opacity-50"
            onClick={() => setPage(page - 1)}
            disabled={page === 0}
          >
            <ChevronLeftIcon />
          </button>

          <button
            type="button"
            data-testid="previous-page-button"
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-secondary hover:bg-secondary-hover active:bg-secondary disabled:bg-transparent disabled:opacity-50"
            onClick={() => setPage(page + 1)}
            disabled={!hasMore}
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}
    </div>
  );
}

export default MembersTable;
